import React from "react";
import { Col, Row } from "react-bootstrap";
import FontAwesome from "react-fontawesome";
import { workFactorAbbrevToId } from "containers/AssessmentSettingsWAA/constants";
import { numberToOrdinalString } from "utils/helperFunctions";
import { dynamicTraitFieldToPretty } from "../constants";

export const getTopFourWorkFactors = (
  testScores,
  candidateFirstName,
  mappedWorkFactors = new Map(),
  listClassName = ""
) => {
  if (!testScores) {
    return null;
  }

  const topFourWorkFactors = Object.keys(workFactorAbbrevToId)
    .filter((abbrev) => testScores.get(abbrev) === 5)
    .sort((a, b) => testScores.get(b) - testScores.get(a))
    .map((workFactorAbbrev) => {
      const workFactor = mappedWorkFactors.get(workFactorAbbrev);
      if (!workFactor) {
        return null;
      }

      return (
        <li key={workFactorAbbrev}>
          <strong>{workFactor.get("name")}: </strong>
          {workFactor.get("description")}
        </li>
      );
    });

  return (
    <>
      <p>
        Of the 20 Work Factors {candidateFirstName} was asked to rank, the top
        four were:{" "}
      </p>
      <ul className={listClassName}>{topFourWorkFactors}</ul>
    </>
  );
};

// Logic taken from api/app/Library/Helpers getRevelianRecommendationV2
const verbalizePercentileAverage = (percentile) => {
  switch (true) {
    case percentile <= 10:
      return "Far Below Average";
    case percentile > 10 && percentile <= 25:
      return "Below Average";
    case percentile > 25 && percentile <= 74:
      return "Average";
    case percentile > 74 && percentile <= 89:
      return "Above Average";
    default:
      return "Far Above Average";
  }
};

export const getCompetencyDetailRows = (reportText, candidateFirstName) => {
  const competencyTexts = reportText.get("competencyText");
  const competencies = reportText.get("competencies");

  const CompetencyDetailRows = [];

  competencyTexts.forEach((competencyText) => {
    const competencyId = competencyText.get("competencyId");

    // competencies have each competency score while competencyTexts do not
    const matchingCompetency = competencies.find(
      (competency) => competency.get("competencyId") === competencyId
    );

    const competencyPercentile =
      matchingCompetency &&
      String(matchingCompetency.get("score").get("percentile"));

    CompetencyDetailRows.push(
      <Row className="mx-1 mb-3" key={competencyId}>
        <Col>
          <Row className="traitDescriptionBox px-2">
            <Col lg={10}>
              <h4 className="reportHeader mt-2 pl-1">
                {competencyText.get("label")}
              </h4>
            </Col>
            {competencyPercentile && (
              <Col
                style={{ borderLeft: "1px solid #B2BBC9", color: "#19355f" }}
                className="font-weight-bold pl-4 text-center"
              >
                <p style={{ fontSize: "14px" }} className="mb-1">
                  {numberToOrdinalString(Number(competencyPercentile))}{" "}
                  Percentile
                </p>
                <p style={{ fontSize: "12px" }} className="mb-0">
                  {verbalizePercentileAverage(Number(competencyPercentile))}
                </p>
              </Col>
            )}
          </Row>
          <Row style={{ margin: "16px 4px 0 4px" }}>
            <p>{competencyText.get("description")}</p>
            <p className="font-weight-bold">
              {`${candidateFirstName}'s results suggest:`}
            </p>
          </Row>
          <ul className="double-list">
            {competencyText.get("competencyByTraitText").map((list, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={i}>{list}</li>
            ))}
          </ul>
        </Col>
      </Row>
    );
  });

  return CompetencyDetailRows;
};

export const getScaleDetailSections = (testId, reportText) => {
  const testScales = {
    15: ["cs", "motivation", "interpersonal", "sales", "work", "inner"],
    24: ["sales", "work", "inner", "motivation", "interpersonal"],
  };

  const FirstScaleDetailSections = [];
  const SecondScaleDetailSections = [];

  testScales[testId].forEach((scale) => {
    const section = (
      <Row key={scale} className="mb-3">
        <Col className="px-3">
          <Row className="mb-3">
            <Col lg={2}>
              <img
                // eslint-disable-next-line global-require
                src={require(
                  `resources/images/dynamic_score_reports/personality/score-report/${scale}-icon.png`
                )}
                title="Score scale icon"
                alt="Score scale icon"
                style={{ height: "64px", width: "64px" }}
              />
            </Col>
            <Col
              style={{
                color: "#19355f",
                fontSize: "16px",
                padding: "20px 8px 0 15px",
              }}
            >
              <strong>{dynamicTraitFieldToPretty[testId][scale]}</strong>
            </Col>
          </Row>
          <Row className="px-2">
            <div
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: reportText.get(`${scale}_text`),
              }}
            />
          </Row>
        </Col>
      </Row>
    );

    if (FirstScaleDetailSections.length < 3) {
      FirstScaleDetailSections.push(section);
    } else {
      SecondScaleDetailSections.push(section);
    }
  });

  return (
    <Row>
      <Col className="px-4">{FirstScaleDetailSections}</Col>
      <Col className="px-4">{SecondScaleDetailSections}</Col>
    </Row>
  );
};

export const getAssessmentErrorAlert = (
  testId,
  inconsistentScore,
  testScores
) => {
  let alertText = `This report may be invalid and should be used with caution. The
      Inconsistent Responding score of ${inconsistentScore} suggests that the
      individual may have been inattentive or careless in making responses.`;

  if (testId === 46) {
    const invalidINC = !testScores.get("validityINC");
    const invalidENH = !testScores.get("validityENH");

    if (invalidINC && invalidENH) {
      alertText =
        "The Inconsistent Responding and Self Enhancement validity checks suggest that this individual may have been inattentive or careless in making responses and may have responded in an overly self-enhancing manner.";
    } else if (invalidINC) {
      alertText =
        "The Inconsistent Responding validity check suggests that this individual may have been inattentive or careless in making responses.";
    } else if (invalidENH) {
      alertText =
        "The Self Enhancement validity check suggests that this individual may have responded in an overly self-enhancing manner.";
    }
  }

  return (
    <Row
      className="text-danger rounded border border-danger py-3 mb-3"
      style={{ backgroundColor: "#faecf1" }}
    >
      <Col lg={4} className="mt-1">
        <h2 className="font-weight-bold ml-3" style={{ fontSize: "20px" }}>
          <FontAwesome name="exclamation-triangle" className="mr-2" />{" "}
          Assessment Error
        </h2>
      </Col>
      <Col>{alertText}</Col>
    </Row>
  );
};
